import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, Pill, Stethoscope, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin-facing view of all medical notes across patients. Read-only.
export const AdminNotesView = () => {
  const { t } = useLanguage();
  const [notes, setNotes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [filterPatient, setFilterPatient] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        // Admin can list all notes by omitting patient_id (per backend logic).
        const [notesRes, usersRes] = await Promise.all([
          axios.get(`${API}/notes`, { withCredentials: true }),
          axios.get(`${API}/admin/users?role=patient`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        setNotes(notesRes.data || []);
        setPatients(usersRes.data || []);
      } catch (e) {
        // noop
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const patientMap = useMemo(() => {
    const m = {};
    patients.forEach((p) => { m[p._id] = p.full_name; });
    return m;
  }, [patients]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notes.filter((n) => {
      if (filterPatient !== 'all' && n.patient_id !== filterPatient) return false;
      if (!q) return true;
      const hay = `${n.notes || ''} ${n.medications || ''} ${n.dosage || ''} ${n.author_name || ''} ${patientMap[n.patient_id] || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [notes, filterPatient, search, patientMap]);

  return (
    <div className="space-y-4" data-testid="admin-notes-view">
      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="relative">
            <Search size={16} className="absolute top-3 start-3 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('ابحث في الملاحظات والوصفات...', 'Search notes & prescriptions...', 'Rechercher dans les notes...')}
              className="ps-9"
              data-testid="admin-notes-search"
            />
          </div>
          <Select value={filterPatient} onValueChange={setFilterPatient}>
            <SelectTrigger data-testid="admin-notes-patient-filter">
              <SelectValue placeholder={t('فلترة حسب المريض', 'Filter by patient', 'Filtrer par patient')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('كل المرضى', 'All patients', 'Tous les patients')}</SelectItem>
              {patients.map((p) => (
                <SelectItem key={p._id} value={p._id}>{p.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3 text-xs text-gray-500">
          {filtered.length} {t('من', 'of', 'sur')} {notes.length} {t('ملاحظة', 'notes', 'notes')}
        </div>
      </Card>

      {loading && (
        <Card className="p-8 text-center text-sm text-gray-500">{t('جاري التحميل...', 'Loading...', 'Chargement...')}</Card>
      )}

      {!loading && filtered.length === 0 && (
        <Card className="p-8 text-center" data-testid="admin-no-notes">
          <FileText className="mx-auto text-gray-300 mb-3" size={48} />
          <p className="text-gray-500 text-sm">{t('لا توجد ملاحظات تطابق البحث', 'No notes match your search', 'Aucune note ne correspond')}</p>
        </Card>
      )}

      {!loading && filtered.map((n) => (
        <Card key={n._id} className="p-5" data-testid={`admin-note-${n._id}`}>
          <div className="flex items-start gap-3 mb-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${n.author_role === 'pharmacist' ? 'bg-purple-100' : 'bg-blue-100'}`}>
              {n.author_role === 'pharmacist'
                ? <Pill size={20} className="text-purple-600" />
                : <Stethoscope size={20} className="text-[#1e88e5]" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-[#0d47a1]">{n.author_name}</p>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 uppercase tracking-wide font-semibold">
                  {n.author_role === 'pharmacist' ? t('صيدلي', 'Pharmacist', 'Pharmacien') : t('طبيب', 'Doctor', 'Médecin')}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('المريض', 'Patient', 'Patient')}: <span className="font-semibold text-gray-700">{patientMap[n.patient_id] || n.patient_id}</span>
                {' • '}
                {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
              </p>
            </div>
          </div>
          {n.medications && (
            <div className="bg-blue-50 rounded-lg p-3 mb-2 text-sm">
              <span className="font-semibold">{t('الأدوية:', 'Medications:', 'Médicaments:')}</span> {n.medications}
            </div>
          )}
          {n.dosage && (
            <div className="bg-blue-50 rounded-lg p-3 mb-2 text-sm">
              <span className="font-semibold">{t('الجرعة:', 'Dosage:', 'Posologie:')}</span> {n.dosage}
            </div>
          )}
          <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">{n.notes}</p>
        </Card>
      ))}
    </div>
  );
};

export default AdminNotesView;
